import { supabase } from "@/lib/supabase"
import { applicationFilterEmbeds, applyApplicationFilters } from "@/services/applicationFilters"
import { FilterState } from "@/types/application"
import { findEarliestBooking, selectActiveBookings } from "@/utils/sessionBookings"

// Lightweight type tailored for CSV export needs
export type StageApplicationForExport = {
  id: string
  application_id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  stage: string
  orientationBooked?: boolean
  orientationBookingDate?: string | null
  vehicleCollectionBooked?: boolean
  vehicleCollectionBookingDate?: string | null
}

// Types mirroring the hook's internal mapping
type ApplicantData = {
  id: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone_number?: string | null
  city?: string | null
  has_electric_van?: boolean | null
  needs_rental_van?: boolean | null
  address_line_1?: string | null
  postcode?: string | null
}

type DepotData = {
  id: string
  name?: string | null
  address?: string | null
  city?: string | null
}

type BookingRow = {
  application_id: string | null
  date?: string | null
  cancelled_at?: string | null
  session?: { type?: string | null } | null
}

interface SupabaseRawRecord {
  id: string
  application_id?: string | null
  current_stage?: string | null
  current_segment?: string | null
  contract_salary?: number | null
  status?: string | null
  created_at?: string | null
  updated_at?: string | null
  applicant: ApplicantData | ApplicantData[] | null
  changed_stage_at?: string | null
  job:
    | {
        id: string
        title?: string | null
        vehicle_type?: string | null
        stages?: string[] | null
        depot: DepotData | DepotData[] | null
      }
    | Array<{
        id: string
        title?: string | null
        vehicle_type?: string | null
        stages?: string[] | null
        depot: DepotData | DepotData[] | null
      }>
    | null
  session_bookings?: BookingRow[] | null
}

export async function fetchAllApplicationsByStage({
  stage,
  searchQuery = "",
  filters = {},
}: {
  stage: string
  searchQuery?: string
  filters?: Partial<FilterState>
}): Promise<StageApplicationForExport[]> {
  const all: StageApplicationForExport[] = []
  const pageSize = 1000
  let from = 0
  let hasMore = true

  const selectColumns = `
    id,
    application_id,
    current_stage,
    current_segment,
    contract_salary,
    status,
    created_at,
    updated_at,
    changed_stage_at,
    applicant:applicant_id(id, first_name, last_name, email, phone_number, address_line_1, city, postcode, has_electric_van, needs_rental_van),
    job:job_id(id, title, vehicle_type, stages, depot:depot_id(id, name, address, city)),
    session_bookings:session_bookings(application_id, date, cancelled_at, session:session_id(type)),
    ${applicationFilterEmbeds(filters, searchQuery)}
    person_id,
    person:people!applications_person_id_fkey(person_id),
  `

  // Fetch in pages until fewer than pageSize are returned
  while (hasMore) {
    let query = supabase
      .from("applications")
      .select(selectColumns)
      .order("changed_stage_at", { ascending: false, nullsFirst: false })

    if (stage && stage !== "all") {
      query = query.eq("current_stage", stage)
    }

    query = applyApplicationFilters(query, filters, searchQuery)

    const to = from + pageSize - 1
    const { data, error } = await query.range(from, to).limit(pageSize)
    if (error) throw error

    const rows = (data || []) as unknown as SupabaseRawRecord[]

    const mapped = rows.map((app: SupabaseRawRecord): StageApplicationForExport => {
      const applicantData = Array.isArray(app.applicant) ? app.applicant[0] : app.applicant

      const applicant = applicantData || {
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        has_electric_van: false,
      }

      // const job = jobData || { id: "", title: "", stages: [] as any };

      let orientationBooked: boolean | undefined = undefined
      let orientationBookingDate: string | null | undefined = undefined
      let vehicleCollectionBooked: boolean | undefined = undefined
      let vehicleCollectionBookingDate: string | null | undefined = undefined
      if (stage === "orientation") {
        const bookings = (app.session_bookings || []) as BookingRow[]
        const orientationBookings = selectActiveBookings(bookings, "orientation")
        orientationBooked = orientationBookings.length > 0
        orientationBookingDate = findEarliestBooking(orientationBookings)?.date ?? null
      }

      if (stage === "vehicle-collection") {
        const bookings = (app.session_bookings || []) as BookingRow[]
        const vcBookings = selectActiveBookings(bookings, "vehicle_collection")
        vehicleCollectionBooked = vcBookings.length > 0
        vehicleCollectionBookingDate = findEarliestBooking(vcBookings)?.date ?? null
      }

      return {
        id: app.id,
        application_id: app.application_id || "",
        firstName: applicant.first_name || "",
        lastName: applicant.last_name || "",
        email: applicant.email || "",
        phone: applicant.phone_number || "",
        stage: app.current_stage || "",
        orientationBooked,
        orientationBookingDate,
        vehicleCollectionBooked,
        vehicleCollectionBookingDate,
      }
    })

    all.push(...mapped)

    if (rows.length < pageSize) {
      hasMore = false
    } else {
      from += pageSize
    }
  }

  return all
}
